import * as React from "react";
import {
  Field,
  Radio,
  RadioGroup,
  Textarea,
  Text,
  Link,
  Checkbox,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from "@fluentui/react-components";
import type { CheckboxProps } from "@fluentui/react-components";

import FileUpload from "../components/FileUpload";
import PrimaryButton from "../components/PrimaryButton";
import Notification from "../components/Notification";

import { getUserTokenIfSignedIn, NotifyFailure } from "../../helpers/auth/auth";
import { buildAPIUrl } from "../../helpers/utils";
import { API_END_POINTS } from "../../helpers/api-paths";
import { BackendResponseError, UserValidationError } from "../../helpers/error-types";
import { EXTERNAL_LINKS } from "../../helpers/external-links";

import {
  backgroundColorSeaGreen,
  backgroundColorSeaGreenActive,
  backgroundColorSeaGreenHover,
  useFormStyles,
  usePageStyles,
} from "../styles/styles";

import { ToasterContext } from "../store/toaster-context-api";
import { UserContext } from "../store/user-contex-api";

const useStyles = makeStyles({
  container: {
    justifyContent: "space-between",
  },

  footer: {
    fontSize: "0.75rem",
    color: tokens.colorNeutralForeground2Link,
  },

  feedbackFooterLink: {
    color: backgroundColorSeaGreen,
    "&:hover": { color: backgroundColorSeaGreenHover },
    "&:hover:active": { color: backgroundColorSeaGreenActive },
  },
});

const Footer = () => {
  const styles = useStyles();
  const { email } = React.useContext(UserContext);
  return (
    <Text className={styles.footer}>
      Signed in as {email}. By pressing Submit, your feedback will be used to improve Geolysis products.
      <Link href={EXTERNAL_LINKS.PRIVACY_POLICY} className={styles.feedbackFooterLink} inline>
        Privacy statement
      </Link>
    </Text>
  );
};

export default function FeedbackPage() {
  const [attachFile, setAttachFile] = React.useState<CheckboxProps["checked"]>(false);
  const [message, setMessage] = React.useState("");
  const [respond, setRespond] = React.useState("yes");
  const [file, setFile] = React.useState(null);
  const [didEditMessage, setDidEditMessage] = React.useState(false);
  const toasterId = React.useContext(ToasterContext);
  const { isAuthenticated, name, email } = React.useContext(UserContext);
  const styles = useStyles();
  const formStyles = useFormStyles();
  const pageStyles = usePageStyles();
  const { dispatchToast } = useToastController(toasterId);

  let messageValidationState = null;
  let messageValidationMessage = "";
  let isFormValid = true;

  const onSubmit = async () => {
    const formData = new FormData();
    formData.append("message", message);
    formData.append("respond", respond);
    formData.append("name", name);
    formData.append("email", email);

    if (file) formData.append("file", file);

    try {
      const url = buildAPIUrl(API_END_POINTS.FEEDBACK);
      let userToken: string = null;

      // User is already signed in.
      userToken = await getUserTokenIfSignedIn();

      if (!userToken) throw new UserValidationError("User validation error.", "Sign in to send feedback");

      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: "Bearer " + userToken },
        body: formData,
        cache: "no-cache",
      });

      if (!response.ok) {
        const error = await response.json();
        const msg = error.message;
        throw new BackendResponseError(msg);
      }

      const result = await response.json();
      dispatchToast(<Notification msg={result.message} />, { intent: "success" });

      // Clear form
      setMessage("");
      setRespond("yes");
      if (attachFile) setAttachFile(false);
      if (file) setFile(null);
      setDidEditMessage(false);
    } catch (error) {
      let todo: string;
      const msg = error.message;

      if (error instanceof UserValidationError) todo = error.todo;
      if (error instanceof BackendResponseError) todo = error.todo;
      NotifyFailure({ msg, todo });
    }
  };

  if (didEditMessage) {
    if (!message) {
      messageValidationState = "error";
      messageValidationMessage = "Please enter message";
      isFormValid = false;
    }
  }

  if (!message) {
    isFormValid = false;
  }

  return (
    <div className={pageStyles.container}>
      <form className={mergeClasses(formStyles.container, styles.container)}>
        <div className="flex flex-col gap-[10px]">
          <Field
            label="What went wrong?"
            validationMessage={messageValidationMessage}
            validationState={messageValidationState}
            required
          >
            <Textarea
              resize="horizontal"
              size="large"
              placeholder="Do not include any private or sensitive information."
              name="msg"
              id="msg"
              style={{ height: "110px" }}
              maxLength={1000}
              onChange={(event) => setMessage(event.target.value)}
              onBlur={() => setDidEditMessage(true)}
              value={message}
              disabled={!isAuthenticated}
            ></Textarea>
          </Field>

          <div className="flex flex-col">
            <Checkbox
              checked={attachFile}
              onChange={(_e, data) => {
                setAttachFile(data.checked);
              }}
              label="Attach file"
              disabled={!isAuthenticated}
            />
            {attachFile && <FileUpload file={file} setFile={setFile} />}
          </div>

          <Field label="May we contact you about your feeback?" required>
            <RadioGroup value={respond} onChange={(_, data) => setRespond(data.value)} disabled={!isAuthenticated}>
              <Radio value="yes" label="Yes, you may contact me" />
              <Radio value="no" label="No, don't contact me" />
            </RadioGroup>
          </Field>
        </div>

        <div className="flex flex-col gap-[5px] pb-[10px]">
          {isAuthenticated && <Footer />}

          <div className="self-end">
            <PrimaryButton onClick={onSubmit} disabled={!isAuthenticated || !isFormValid}>
              Submit
            </PrimaryButton>
          </div>
        </div>
      </form>
    </div>
  );
}
